/* eslint-disable react/jsx-props-no-spreading */
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import Button from '../../elements/Button';
import { DEFAULT_TABLE_FILTERS, normalizeTableFilters } from './tableFilters';

function OptionPill({ active, onClick, children }) {
  const optionClassName = [
    'w-full rounded-xl px-3 py-2 text-left text-sm font-medium leading-tight transition',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brandBlue-200',
    'sm:w-auto sm:whitespace-nowrap sm:text-center',
    active ? 'bg-brandBlue-600 text-white shadow-sm' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
  ].join(' ');

  return (
    <button
      type="button"
      onClick={onClick}
      className={optionClassName}
    >
      {children}
    </button>
  );
}
OptionPill.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};
OptionPill.defaultProps = {
  active: false,
  onClick: () => {},
};

export default function TableFilterDialog({ open, currentFilters, onApply, onCancel }) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'tableFiltersDialog' });

  const [draftFilters, setDraftFilters] = useState(normalizeTableFilters(currentFilters));

  useEffect(() => {
    if (!open) return;
    setDraftFilters(normalizeTableFilters(currentFilters));
  }, [open, currentFilters]);

  const hasChanges = useMemo(() => {
    const normalizedCurrent = normalizeTableFilters(currentFilters);
    const normalizedDraft = normalizeTableFilters(draftFilters);
    return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedDraft);
  }, [currentFilters, draftFilters]);

  const amountOptions = [
    { id: 'all', label: t('amountAny') },
    { id: 'inflow', label: t('amountInflow') },
    { id: 'outflow', label: t('amountOutflow') },
    { id: 'zero', label: t('amountZero') },
  ];

  const dateOptions = [
    { id: 'all', label: t('dateAll') },
    { id: '30d', label: t('date30d') },
    { id: '90d', label: t('date90d') },
    { id: '365d', label: t('date365d') },
  ];

  const inputClassName = [
    'mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm',
    'placeholder:text-gray-400 focus:border-brandBlue-400 focus:outline-none focus:ring-2 focus:ring-brandBlue-100',
  ].join(' ');

  const panelClassName = [
    'mx-2 w-[calc(100vw-1rem)] max-w-xl transform overflow-x-hidden overflow-y-auto rounded-2xl',
    'bg-white p-4 text-left shadow-xl transition-all max-h-[92dvh]',
    'sm:mx-0 sm:w-full sm:p-6',
  ].join(' ');

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[80] overflow-y-auto" onClose={onCancel}>
        <div className="min-h-full">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Dialog.Overlay className="fixed inset-0 bg-gray-500/70 transition-opacity" />
          </Transition.Child>

          <div className="relative flex min-h-full items-center justify-center p-2 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={panelClassName}>
                <Dialog.Title className="text-lg font-semibold text-gray-900">{t('title')}</Dialog.Title>
                <p className="mt-1 text-sm text-gray-500">{t('description')}</p>

                <div className="mt-5 space-y-5">
                  <div>
                    <label htmlFor="table-filter-query" className="block text-sm font-semibold text-gray-800">
                      {t('searchLabel')}
                    </label>
                    <input
                      id="table-filter-query"
                      type="text"
                      value={draftFilters.query}
                      onChange={(event) => setDraftFilters((prev) => ({ ...prev, query: event.target.value }))}
                      placeholder={t('searchPlaceholder')}
                      className={inputClassName}
                    />
                  </div>

                  <fieldset>
                    <legend className="block text-sm font-semibold text-gray-800">{t('amountLabel')}</legend>
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                      {amountOptions.map((option) => (
                        <OptionPill
                          key={option.id}
                          active={draftFilters.amountDirection === option.id}
                          onClick={() => setDraftFilters((prev) => ({ ...prev, amountDirection: option.id }))}
                        >
                          {option.label}
                        </OptionPill>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset>
                    <legend className="block text-sm font-semibold text-gray-800">{t('dateLabel')}</legend>
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                      {dateOptions.map((option) => (
                        <OptionPill
                          key={option.id}
                          active={draftFilters.dateRange === option.id}
                          onClick={() => setDraftFilters((prev) => ({ ...prev, dateRange: option.id }))}
                        >
                          {option.label}
                        </OptionPill>
                      ))}
                    </div>
                  </fieldset>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button text={t('reset')} size="md" onClick={() => setDraftFilters({ ...DEFAULT_TABLE_FILTERS })} />
                  <Button text={t('cancel')} size="md" onClick={onCancel} />
                  <Button text={t('apply')} size="md" withAccent enabled={hasChanges} onClick={() => onApply(draftFilters)} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
TableFilterDialog.propTypes = {
  open: PropTypes.bool,
  currentFilters: PropTypes.shape({
    query: PropTypes.string,
    amountDirection: PropTypes.string,
    dateRange: PropTypes.string,
  }),
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
};
TableFilterDialog.defaultProps = {
  open: false,
  currentFilters: DEFAULT_TABLE_FILTERS,
  onApply: () => {},
  onCancel: () => {},
};
